import React, { useEffect, useState } from 'react'
import { compressImage } from '../utils/imageCompression'
import { supabaseClient } from '../config/supabase'
import { useAdminScope } from '../hooks/useAdminScope'
import ReactQuill, { Quill } from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import ImageResize from 'quill-image-resize-module-react'
import {
  Newspaper,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Search,
  Image as ImageIcon,
  Calendar,
  User,
  X,
  Save,
  AlertCircle,
  Clock,
  Archive,
} from 'lucide-react'

// News Article Type
interface NewsArticle {
  id: string
  title: string
  slug: string
  excerpt: string | null
  content: string
  image_url: string | null
  category: string
  department: string | null
  author_id: string | null
  author_name: string | null
  status: 'draft' | 'published' | 'archived'
  published_at: string | null
  created_at: string
  updated_at: string
}

const NEWS_CATEGORIES = ['General', 'Awards', 'Events', 'Partnership']
const NEWS_STATUSES: Array<'draft' | 'published' | 'archived'> = ['draft', 'published', 'archived']

// Register ImageResize module
Quill.register('modules/imageResize', ImageResize)

// Rich Text Editor Configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link'],
    [{ 'color': [] }, { 'background': [] }],
    ['clean']
  ],
  imageResize: {
    parchment: Quill.import('parchment'),
    modules: ['Resize', 'DisplaySize'],
    handleStyles: {
      backgroundColor: 'black',
      border: 'none',
      color: 'white'
    },
    displayStyles: {
      backgroundColor: 'black',
      border: 'none',
      color: 'white',
      padding: '4px 8px',
      borderRadius: '4px'
    }
  }
}

const quillFormats = [
  'header',
  'size',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'align',
  'link', 'image',
  'color', 'background',
  'width', 'height', 'style'
]

export default function ManageNews() {
  const { isSuperAdmin, allowedDepartments, department: adminDepartment, loading: scopeLoading } = useAdminScope()
  const [news, setNews] = useState<NewsArticle[]>([])
  const [filteredNews, setFilteredNews] = useState<NewsArticle[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('published')
  const [filterDepartment, setFilterDepartment] = useState<string>('all')
  const [availableDepartments, setAvailableDepartments] = useState<string[]>([])  // from DB departments table
  const [formDepartments, setFormDepartments] = useState<{ id: string; name: string }[]>([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string>('')
  const [uploading, setUploading] = useState(false)
  
  // Content image insertion
  const [insertingImage, setInsertingImage] = useState(false)
  const contentTextareaRef = React.useRef<ReactQuill>(null)

  // Modal states
  const [showNewsModal, setShowNewsModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [messageModal, setMessageModal] = useState<{
    title: string
    message: string
    type: 'error' | 'success' | 'warning'
  }>({
    title: '',
    message: '',
    type: 'error',
  })
  const [editingNews, setEditingNews] = useState<NewsArticle | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null)
  const [viewingNews, setViewingNews] = useState<NewsArticle | null>(null)
  
  // Confirmation modal states
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [showUnpublishModal, setShowUnpublishModal] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  const [confirmTarget, setConfirmTarget] = useState<NewsArticle | null>(null)
  const [restoreTarget, setRestoreTarget] = useState<{ article: NewsArticle; targetStatus: 'published' | 'draft' } | null>(null)

  // Form state
  const [newsForm, setNewsForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    image_url: '',
    category: 'General',
    status: 'draft' as 'draft' | 'published' | 'archived',
    department: null as string | null,
  })

  // Helper: can this admin modify this article?
  const canModify = (article: NewsArticle): boolean => {
    if (isSuperAdmin) return true
    if (!allowedDepartments || allowedDepartments.length === 0) return false
    return article.department !== null && allowedDepartments.includes(article.department)
  }

  useEffect(() => {
    if (!scopeLoading) {
      fetchNews()
      if (isSuperAdmin) fetchAllDepartments()
    }
  }, [isSuperAdmin, allowedDepartments, scopeLoading])

  useEffect(() => {
    filterNewsArticles()
  }, [news, searchQuery, filterCategory, filterStatus, filterDepartment, isSuperAdmin])

  const showMessage = (
    title: string,
    message: string,
    type: 'error' | 'success' | 'warning' = 'error'
  ) => {
    setMessageModal({ title, message, type })
    setShowMessageModal(true)
  }

  // Generate URL-friendly slug from title
  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces/underscores with hyphens
      .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
      + '-' + Date.now() // Add timestamp to ensure uniqueness
  }

  // Clean empty HTML content from ReactQuill
  const cleanEmptyHtml = (html: string): string | null => {
    if (!html) return null
    // Remove all HTML tags and check if there's actual content
    const text = html.replace(/<[^>]*>/g, '').trim()
    return text ? html : null
  }

  // Strip HTML tags for display
  const stripHtml = (html: string | null): string => {
    if (!html) return ''
    const text = html.replace(/<[^>]*>/g, '').trim()
    return text || ''
  }

  // Get time ago string
  const getTimeAgo = (dateString: string | null): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (seconds < 60) return 'Just now'
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 30) return `${days}d ago`
    const months = Math.floor(days / 30)
    if (months < 12) return `${months}mo ago`
    const years = Math.floor(months / 12)
    return `${years}y ago`
  }

  // Fetch all department names (for super-admin scope filter + form dropdown)
  const fetchAllDepartments = async () => {
    try {
      const { data, error } = await supabaseClient
        .from('departments')
        .select('id, name')
        .eq('is_active', true)
        .order('name', { ascending: true })
      if (error) throw error
      setFormDepartments(data || [])
      setAvailableDepartments((data || []).map((d: any) => d.name))
    } catch (err) {
      console.error('Error fetching departments:', err)
    }
  }

  const fetchNews = async () => {
    try {
      setLoading(true)
      setError(null)

      let query = supabaseClient
        .from('news')
        .select('*')
        .order('created_at', { ascending: false })

      // Dept admins only see news targeted to their department (not global)
      if (!isSuperAdmin && allowedDepartments && allowedDepartments.length > 0) {
        query = query.in('department', allowedDepartments)
      }

      const { data, error: fetchError } = await query
      if (fetchError) throw fetchError

      setNews(data || [])
    } catch (err: any) {
      console.error('Error fetching news:', err)
      setError(err.message || 'Failed to load news articles')
    } finally {
      setLoading(false)
    }
  }

  const filterNewsArticles = () => {
    let filtered = [...news]

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.excerpt?.toLowerCase().includes(query) ||
          article.content.toLowerCase().includes(query) ||
          article.category.toLowerCase().includes(query)
      )
    }

    // Category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter((article) => article.category === filterCategory)
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((article) => article.status === filterStatus)
    }

    // Department filter (super admin only)
    if (isSuperAdmin && filterDepartment !== 'all') {
      if (filterDepartment === 'global') {
        filtered = filtered.filter((article) => article.department === null)
      } else {
        filtered = filtered.filter((article) => article.department === filterDepartment)
      }
    }

    setFilteredNews(filtered)
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        showMessage('Error', 'Image size must be less than 5MB', 'error')
        return
      }
      if (!file.type.startsWith('image/')) {
        showMessage('Error', 'Please select an image file', 'error')
        return
      }
      setSelectedFile(file)
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const uploadNewsImage = async (): Promise<string | null> => {
    if (!selectedFile) return null

    try {
      setUploading(true)
      const compressed = await compressImage(selectedFile, { maxWidth: 1200, maxHeight: 1200, quality: 0.85 })
      const fileName = `news-${Date.now()}.jpg`
      const filePath = `news-images/${fileName}`

      // Upload to Supabase storage
      const { error: uploadError } = await supabaseClient.storage
        .from('news-images')
        .upload(filePath, compressed, {
          upsert: true,
          contentType: 'image/jpeg'
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        
        // Provide helpful error messages
        if (uploadError.message.includes('not found') || uploadError.message.includes('bucket')) {
          showMessage('Error', 'Storage bucket not set up. Please create "news-images" bucket in Supabase Storage.', 'error')
        } else if (uploadError.message.includes('policy')) {
          showMessage('Error', 'Permission denied. Please check storage policies in Supabase.', 'error')
        } else {
          showMessage('Error', `Upload failed: ${uploadError.message}`, 'error')
        }
        throw uploadError
      }

      // Get public URL
      const { data } = supabaseClient.storage
        .from('news-images')
        .getPublicUrl(filePath)

      return data.publicUrl
    } catch (error: any) {
      console.error('Error uploading image:', error)
      return null
    } finally {
      setUploading(false)
    }
  }

  const handleContentImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        showMessage('Error', 'Image size must be less than 5MB', 'error')
        return
      }
      if (!file.type.startsWith('image/')) {
        showMessage('Error', 'Please select an image file', 'error')
        return
      }
      // Directly insert the image
      await insertImageIntoContent(file)
    }
    // Reset the input so the same file can be selected again
    e.target.value = ''
  }

  const insertImageIntoContent = async (file: File) => {
    try {
      setInsertingImage(true)

      // Upload image
      const compressed = await compressImage(file, { maxWidth: 1200, maxHeight: 1200, quality: 0.85 })
      const fileName = `news-content-${Date.now()}.jpg`
      const filePath = `news-images/${fileName}`

      const { error: uploadError } = await supabaseClient.storage
        .from('news-images')
        .upload(filePath, compressed, {
          upsert: true,
          contentType: 'image/jpeg'
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        if (uploadError.message.includes('not found') || uploadError.message.includes('bucket')) {
          showMessage('Error', 'Storage bucket not set up. Please create "news-images" bucket in Supabase Storage.', 'error')
        } else if (uploadError.message.includes('policy')) {
          showMessage('Error', 'Permission denied. Please check storage policies in Supabase.', 'error')
        } else {
          showMessage('Error', `Upload failed: ${uploadError.message}`, 'error')
        }
        return
      }

      // Get public URL
      const { data } = supabaseClient.storage
        .from('news-images')
        .getPublicUrl(filePath)

      // Insert image using ReactQuill's insertEmbed for proper image handling
      const quillEditor = contentTextareaRef.current
      
      if (quillEditor) {
        try {
          // Get the Quill instance
          const quill = (quillEditor as any).getEditor()
          if (quill) {
            const range = quill.getSelection(true)
            const position = range ? range.index : quill.getLength()
            
            // Insert image at cursor position - will be resizable thanks to imageResize module
            quill.insertEmbed(position, 'image', data.publicUrl, 'user')
            
            // Add a line break after the image so text continues on next line
            quill.insertText(position + 1, '\n', 'user')
            
            // Move cursor after the image and line break
            quill.setSelection(position + 2)
            
            showMessage('Success', 'Image inserted! Drag the corners to resize it.', 'success')
          } else {
            throw new Error('Quill editor not initialized')
          }
        } catch (err) {
          console.error('Error accessing Quill editor:', err)
          showMessage('Error', 'Failed to insert image into editor', 'error')
        }
      } else {
        showMessage('Error', 'Editor not ready', 'error')
      }
    } catch (error: any) {
      console.error('Error inserting image:', error)
      showMessage('Error', 'Failed to insert image', 'error')
    } finally {
      setInsertingImage(false)
    }
  }

  const openAddNewsModal = () => {
    setEditingNews(null)
    setNewsForm({
      title: '',
      excerpt: '',
      content: '',
      image_url: '',
      category: 'General',
      status: 'draft',
      // Dept admins are locked to their department; super admin defaults to global (null)
      department: isSuperAdmin ? null : (allowedDepartments?.[0] ?? null),
    })
    setSelectedFile(null)
    setImagePreview('')
    setShowNewsModal(true)
  }

  const openEditNewsModal = (article: NewsArticle) => {
    setEditingNews(article)
    setNewsForm({
      title: article.title,
      excerpt: article.excerpt || '',
      content: article.content,
      image_url: article.image_url || '',
      category: article.category,
      status: article.status,
      department: article.department,
    })
    setSelectedFile(null)
    setImagePreview(article.image_url || '')
    setShowNewsModal(true)
  }

  const handleSaveNews = async () => {
    try {
      if (!newsForm.title.trim()) {
        showMessage('Validation Error', 'Title is required', 'error')
        return
      }

      // Check if content has actual text (not just empty HTML tags)
      if (!cleanEmptyHtml(newsForm.content)) {
        showMessage('Validation Error', 'Content is required', 'error')
        return
      }

      // Upload image if a new file is selected
      let imageUrl = newsForm.image_url
      if (selectedFile) {
        const uploadedUrl = await uploadNewsImage()
        if (uploadedUrl) {
          imageUrl = uploadedUrl
        } else {
          showMessage('Error', 'Failed to upload image', 'error')
          return
        }
      }

      // Get current user for author info
      const {
        data: { user },
      } = await supabaseClient.auth.getUser()

      // Generate slug for new articles or when title changes
      const slug = editingNews ? undefined : generateSlug(newsForm.title)

      const articleData: any = {
        title: newsForm.title.trim(),
        excerpt: cleanEmptyHtml(newsForm.excerpt) || null,
        content: newsForm.content.trim(),
        image_url: imageUrl || null,
        category: newsForm.category,
        status: newsForm.status,
        // For dept admins, always force their department; super admin uses form value
        department: isSuperAdmin ? (newsForm.department || null) : (allowedDepartments?.[0] ?? null),
        author_id: user?.id || null,
        author_name: user?.email || null,
      }

      // Handle published_at timestamp
      if (newsForm.status === 'published') {
        // If changing status to published (from draft/archived) OR creating new published article, set new timestamp
        if (!editingNews || editingNews.status !== 'published') {
          articleData.published_at = new Date().toISOString()
        } else {
          // Keep existing timestamp if already published
          articleData.published_at = editingNews.published_at
        }
      } else {
        // Preserve existing timestamp for draft/archived articles
        articleData.published_at = editingNews?.published_at || null
      }

      // Add slug only for new articles
      if (slug) {
        articleData.slug = slug
      }

      if (editingNews) {
        // Update existing
        const { error: updateError } = await supabaseClient
          .from('news')
          .update(articleData)
          .eq('id', editingNews.id)

        if (updateError) throw updateError

        showMessage('Success', 'News article updated successfully', 'success')
      } else {
        // Create new
        const { error: insertError } = await supabaseClient.from('news').insert([articleData])

        if (insertError) throw insertError

        showMessage('Success', 'News article created successfully', 'success')
      }

      setShowNewsModal(false)
      fetchNews()
    } catch (err: any) {
      console.error('Error saving news:', err)
      showMessage('Error', err.message || 'Failed to save news article', 'error')
    }
  }

  const openDeleteNewsModal = (article: NewsArticle) => {
    setDeleteTarget({ id: article.id, title: article.title })
    setShowDeleteModal(true)
  }

  const handleDeleteNews = async () => {
    if (!deleteTarget) return

    try {
      const { error: deleteError } = await supabaseClient
        .from('news')
        .delete()
        .eq('id', deleteTarget.id)

      if (deleteError) throw deleteError

      showMessage('Success', 'News article deleted successfully', 'success')
      setShowDeleteModal(false)
      setDeleteTarget(null)
      fetchNews()
    } catch (err: any) {
      console.error('Error deleting news:', err)
      showMessage('Error', err.message || 'Failed to delete news article', 'error')
    }
  }

  const openViewModal = (article: NewsArticle) => {
    setViewingNews(article)
    setShowViewModal(true)
  }

  const openEditFromView = () => {
    if (viewingNews) {
      setShowViewModal(false)
      openEditNewsModal(viewingNews)
    }
  }

  const handleArchiveNews = async () => {
    if (!confirmTarget && !viewingNews) return
    
    const target = confirmTarget || viewingNews
    if (!target) return
    
    try {
      const { error: updateError } = await supabaseClient
        .from('news')
        .update({ status: 'archived' })
        .eq('id', target.id)

      if (updateError) throw updateError

      showMessage('Success', 'News article archived successfully', 'success')
      setShowArchiveModal(false)
      setConfirmTarget(null)
      setShowViewModal(false)
      setViewingNews(null)
      fetchNews()
    } catch (err: any) {
      console.error('Error archiving news:', err)
      showMessage('Error', err.message || 'Failed to archive news article', 'error')
    }
  }

  const handlePublishNews = async () => {
    if (!confirmTarget) return
    
    try {
      const updateData: any = { 
        status: 'published',
        published_at: new Date().toISOString()
      }

      const { error: updateError } = await supabaseClient
        .from('news')
        .update(updateData)
        .eq('id', confirmTarget.id)

      if (updateError) throw updateError

      showMessage('Success', 'News article published successfully', 'success')
      setShowPublishModal(false)
      setConfirmTarget(null)
      fetchNews()
    } catch (err: any) {
      console.error('Error publishing news:', err)
      showMessage('Error', err.message || 'Failed to publish news article', 'error')
    }
  }

  const handleUnpublishNews = async () => {
    if (!confirmTarget) return
    
    try {
      const { error: updateError } = await supabaseClient
        .from('news')
        .update({ status: 'draft' })
        .eq('id', confirmTarget.id)

      if (updateError) throw updateError

      showMessage('Success', 'News article unpublished successfully', 'success')
      setShowUnpublishModal(false)
      setConfirmTarget(null)
      fetchNews()
    } catch (err: any) {
      console.error('Error unpublishing news:', err)
      showMessage('Error', err.message || 'Failed to unpublish news article', 'error')
    }
  }

  const openPublishModal = (article: NewsArticle) => {
    setConfirmTarget(article)
    setShowPublishModal(true)
  }

  const openUnpublishModal = (article: NewsArticle) => {
    setConfirmTarget(article)
    setShowUnpublishModal(true)
  }

  const openArchiveModal = (article: NewsArticle) => {
    setConfirmTarget(article)
    setShowArchiveModal(true)
  }

  const openRestoreModal = (article: NewsArticle, targetStatus: 'published' | 'draft') => {
    setRestoreTarget({ article, targetStatus })
    setShowRestoreModal(true)
  }

  const handleRestoreNews = async () => {
    if (!restoreTarget) return
    
    const { article, targetStatus } = restoreTarget
    
    try {
      const updateData: any = { 
        status: targetStatus
      }
      
      // Set published_at timestamp when restoring to published
      if (targetStatus === 'published') {
        updateData.published_at = new Date().toISOString()
      }

      const { error: updateError } = await supabaseClient
        .from('news')
        .update(updateData)
        .eq('id', article.id)

      if (updateError) throw updateError

      const statusText = targetStatus === 'published' ? 'published' : 'draft'
      showMessage('Success', `News article restored to ${statusText} successfully`, 'success')
      setShowRestoreModal(false)
      setRestoreTarget(null)
      setShowViewModal(false)
      setViewingNews(null)
      fetchNews()
    } catch (err: any) {
      console.error('Error restoring news:', err)
      showMessage('Error', err.message || 'Failed to restore news article', 'error')
    }
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Not set'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getStatusBadgeColor = (status: string): string => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800'
      case 'draft':
        return 'bg-yellow-100 text-yellow-800'
      case 'archived':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'Awards':
        return 'bg-purple-100 text-purple-800'
      case 'Events':
        return 'bg-blue-100 text-blue-800'
      case 'Partnership':
        return 'bg-indigo-100 text-indigo-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading news articles...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
          <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
          <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                <Newspaper className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Manage News & Updates</h1>
                <p className="text-sm text-blue-200 mt-1">Create and manage news articles for Trailblaze.</p>
              </div>
            </div>
            <button
              onClick={openAddNewsModal}
              className="flex items-center gap-2 bg-yellow-400 text-gray-900 px-4 py-2 rounded-lg hover:bg-yellow-300 transition shrink-0 font-semibold"
            >
              <Plus className="h-5 w-5" />
              Create News Article
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search news..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {NEWS_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              {NEWS_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Department filter — super admin only */}
          {isSuperAdmin && (
            <div className="mt-3 flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Scope:</label>
              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">All Scopes</option>
                <option value="global">🌐 Global only</option>
                {availableDepartments.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Total Articles</p>
            <p className="text-2xl font-bold text-gray-900">{news.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Published</p>
            <p className="text-2xl font-bold text-green-600">
              {news.filter((n) => n.status === 'published').length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Drafts</p>
            <p className="text-2xl font-bold text-yellow-600">
              {news.filter((n) => n.status === 'draft').length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Archived</p>
            <p className="text-2xl font-bold text-gray-600">
              {news.filter((n) => n.status === 'archived').length}
            </p>
          </div>
        </div>

        {/* News Grid */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {filteredNews.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <Newspaper className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchQuery || filterCategory !== 'all' || filterStatus !== 'all'
                ? 'No news articles match your filters'
                : 'No news articles yet. Click "Create News Article" to create one.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredNews.map((article) => (
              <div key={article.id} className="bg-white rounded-lg shadow hover:shadow-lg transition flex flex-col">
                {/* Image */}
                {article.image_url ? (
                  <img
                    src={article.image_url}
                    alt={article.title}
                    className="w-full h-48 object-cover rounded-t-lg"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-200 rounded-t-lg flex items-center justify-center">
                    <ImageIcon className="h-16 w-16 text-gray-400" />
                  </div>
                )}

                {/* Content */}
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs px-2 py-1 rounded ${getCategoryColor(article.category)}`}>
                      {article.category}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${getStatusBadgeColor(article.status)}`}>
                      {article.status === 'published' && <Eye className="inline h-3 w-3 mr-1" />}
                      {article.status === 'draft' && <EyeOff className="inline h-3 w-3 mr-1" />}
                      {article.status.charAt(0).toUpperCase() + article.status.slice(1)}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      article.department ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {article.department ?? '🌐 Global'}
                    </span>
                  </div>

                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{article.title}</h3>

                  {/* Excerpt if available */}
                  {article.excerpt && stripHtml(article.excerpt) && (
                    <p className="text-xs text-gray-600 mb-2 italic line-clamp-2">
                      {stripHtml(article.excerpt)}
                    </p>
                  )}

                  {/* Content Preview */}
                  <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                    {stripHtml(article.content).substring(0, 150)}...
                  </p>

                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(article.published_at || article.created_at)} • {getTimeAgo(article.published_at || article.created_at)}
                    </div>
                    {article.author_name && (
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {article.author_name.split('@')[0]}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-3 border-t border-gray-200 mt-auto">
                    <button
                      onClick={() => openViewModal(article)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded transition"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </button>
                    
                    {/* Draft Actions */}
                    {article.status === 'draft' && canModify(article) && (
                      <>
                        <button
                          onClick={() => openPublishModal(article)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded transition"
                        >
                          <Eye className="h-4 w-4" />
                          Publish
                        </button>
                        <button
                          onClick={() => openDeleteNewsModal(article)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </>
                    )}
                    
                    {/* Published Actions */}
                    {article.status === 'published' && canModify(article) && (
                      <>
                        <button
                          onClick={() => openUnpublishModal(article)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-yellow-600 hover:bg-yellow-50 rounded transition"
                        >
                          <EyeOff className="h-4 w-4" />
                          Unpublish
                        </button>
                        <button
                          onClick={() => openArchiveModal(article)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded transition"
                        >
                          <Archive className="h-4 w-4" />
                          Archive
                        </button>
                      </>
                    )}
                    
                    {/* Archived Actions */}
                    {article.status === 'archived' && canModify(article) && (
                      <>
                        <button
                          onClick={() => openDeleteNewsModal(article)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* News Modal */}
      {showNewsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-20 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 flex-shrink-0">
                {editingNews ? 'Edit News Article' : 'Add News Article'}
              </h2>
              <button
                onClick={() => setShowNewsModal(false)}
                className="p-2 hover:bg-gray-100 rounded transition flex-shrink-0 ml-4"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newsForm.title}
                  onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter article title"
                />
              </div>

              {/* Category and Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newsForm.category}
                    onChange={(e) => setNewsForm({ ...newsForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {NEWS_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={newsForm.status}
                    onChange={(e) =>
                      setNewsForm({
                        ...newsForm,
                        status: e.target.value as 'draft' | 'published' | 'archived',
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {NEWS_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Department scope */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Audience Scope</label>
                {isSuperAdmin ? (
                  <select
                    value={newsForm.department ?? 'global'}
                    onChange={(e) =>
                      setNewsForm({ ...newsForm, department: e.target.value === 'global' ? null : e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="global">🌐 Global (all alumni &amp; public)</option>
                    {formDepartments.map((dept) => (
                      <option key={dept.id} value={dept.name}>{dept.name}</option>
                    ))}
                  </select>
                ) : (
                  <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 font-medium">
                    {allowedDepartments?.[0] ?? adminDepartment ?? 'Your Department'}
                    <span className="ml-2 text-xs font-normal text-blue-600">(automatically assigned)</span>
                  </div>
                )}
                {!isSuperAdmin && (
                  <p className="text-xs text-gray-500 mt-1">
                    As a department admin, your articles are automatically scoped to your department.
                  </p>
                )}
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Featured Image
                </label>
                <div className="space-y-3">
                  {imagePreview && (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-lg border border-gray-300"
                        onError={(e) => {
                          e.currentTarget.src = ''
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFile(null)
                          setImagePreview('')
                          setNewsForm({ ...newsForm, image_url: '' })
                        }}
                        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <p className="text-xs text-gray-500">
                    Select an image file (max 5MB). Supported formats: JPG, PNG, GIF
                  </p>
                </div>
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
                <ReactQuill
                  theme="snow"
                  value={newsForm.excerpt}
                  onChange={(value) => setNewsForm({ ...newsForm, excerpt: value })}
                  modules={{
                    toolbar: [
                      ['bold', 'italic', 'underline'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      ['clean']
                    ]
                  }}
                  formats={['bold', 'italic', 'underline', 'list', 'bullet']}
                  placeholder="Short summary of the article"
                  className="bg-white [&_.ql-editor]:overflow-y-auto [&_.ql-editor]:max-h-[120px]"
                  style={{ marginBottom: '50px' }}
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <ReactQuill
                    ref={contentTextareaRef}
                    theme="snow"
                    value={newsForm.content}
                    onChange={(value) => setNewsForm({ ...newsForm, content: value })}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Full article content with rich text formatting"
                    className="bg-white [&_.ql-editor]:overflow-y-auto [&_.ql-editor]:max-h-[300px]"
                    style={{ marginBottom: '50px' }}
                  />
                  {/* Insert Image Button - Positioned within toolbar area */}
                  <div className="absolute top-0 right-0 z-10">
                    <input
                      type="file"
                      id="content-image-upload"
                      accept="image/*"
                      onChange={handleContentImageSelect}
                      className="hidden"
                    />
                    <label
                      htmlFor="content-image-upload"
                      className={`flex items-center gap-1.5 px-3 py-1.5 m-2 border border-gray-300 rounded text-xs font-medium transition cursor-pointer ${
                        insertingImage
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                      title="Insert image at cursor position"
                    >
                      {insertingImage ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600"></div>
                          <span className="hidden sm:inline">Inserting...</span>
                        </>
                      ) : (
                        <>
                          <ImageIcon className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Insert Image</span>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200 z-20">
              <button
                onClick={() => setShowNewsModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNews}
                disabled={uploading}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    {editingNews ? 'Update' : 'Create'} Article
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Article Modal */}
      {showViewModal && viewingNews && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto my-8">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(viewingNews.category)}`}>
                  {viewingNews.category}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(viewingNews.status)}`}>
                  {viewingNews.status.charAt(0).toUpperCase() + viewingNews.status.slice(1)}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setViewingNews(null)
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition flex-shrink-0"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              {/* Featured Image */}
              {viewingNews.image_url && (
                <div className="relative h-64 md:h-96 rounded-lg overflow-hidden mb-8">
                  <img
                    src={viewingNews.image_url}
                    alt={viewingNews.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Article Meta */}
              <div className="flex items-center gap-4 text-sm text-gray-600 mb-6 flex-wrap">
                {viewingNews.author_name && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span className="font-medium">
                      {viewingNews.author_name.split('@')[0]}
                    </span>
                  </div>
                )}
                <span className="text-gray-400">•</span>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(viewingNews.published_at || viewingNews.created_at)}</span>
                </div>
                <span className="text-gray-400">•</span>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{getTimeAgo(viewingNews.published_at || viewingNews.created_at)}</span>
                </div>
              </div>

              {/* Article Title */}
              <h1 className="text-4xl font-serif font-bold text-gray-900 mb-6 leading-tight">
                {viewingNews.title}
              </h1>

              {/* Article Excerpt */}
              {viewingNews.excerpt && stripHtml(viewingNews.excerpt) && (
                <p className="text-xl text-gray-600 mb-8 leading-relaxed border-l-4 border-blue-500 pl-4 italic">
                  {stripHtml(viewingNews.excerpt)}
                </p>
              )}

              {/* Article Content */}
              <div className="prose prose-lg max-w-none">
                <div 
                  className="text-gray-700 leading-relaxed article-content-view"
                  dangerouslySetInnerHTML={{ __html: viewingNews.content }}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 mt-8 pt-6 border-t border-gray-200 flex-wrap">
                {/* Edit button - only if user can modify this article */}
                {canModify(viewingNews) && (
                  <button
                    onClick={openEditFromView}
                    className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
                  >
                    <Edit className="h-5 w-5" />
                    Edit Article
                  </button>
                )}
                
                {/* Draft Actions */}
                {viewingNews.status === 'draft' && canModify(viewingNews) && (
                  <>
                    <button
                      onClick={() => {
                        setShowViewModal(false)
                        openPublishModal(viewingNews)
                      }}
                      className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition"
                    >
                      <Eye className="h-5 w-5" />
                      Publish
                    </button>
                    <button
                      onClick={() => {
                        setShowViewModal(false)
                        openDeleteNewsModal(viewingNews)
                      }}
                      className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition"
                    >
                      <Trash2 className="h-5 w-5" />
                      Delete
                    </button>
                  </>
                )}
                
                {/* Published Actions */}
                {viewingNews.status === 'published' && canModify(viewingNews) && (
                  <>
                    <button
                      onClick={() => {
                        setShowViewModal(false)
                        openUnpublishModal(viewingNews)
                      }}
                      className="flex items-center gap-2 bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition"
                    >
                      <EyeOff className="h-5 w-5" />
                      Unpublish
                    </button>
                    <button
                      onClick={() => {
                        setShowViewModal(false)
                        openArchiveModal(viewingNews)
                      }}
                      className="flex items-center gap-2 bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition"
                    >
                      <Archive className="h-5 w-5" />
                      Archive
                    </button>
                  </>
                )}
                
                {/* Archived Actions */}
                {viewingNews.status === 'archived' && canModify(viewingNews) && (
                  <>
                    <button
                      onClick={() => openRestoreModal(viewingNews, 'published')}
                      className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition"
                    >
                      <Eye className="h-5 w-5" />
                      Restore &amp; Publish
                    </button>
                    <button
                      onClick={() => openRestoreModal(viewingNews, 'draft')}
                      className="flex items-center gap-2 bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition"
                    >
                      <Edit className="h-5 w-5" />
                      Restore to Draft
                    </button>
                    <button
                      onClick={() => {
                        setShowViewModal(false)
                        openDeleteNewsModal(viewingNews)
                      }}
                      className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition"
                    >
                      <Trash2 className="h-5 w-5" />
                      Delete
                    </button>
                  </>
                )}
                
                {/* Close button */}
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    setViewingNews(null)
                  }}
                  className="flex items-center gap-2 px-6 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete News Article</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete "{deleteTarget.title}"? This action cannot be undone.
                </p>
                <div className="flex items-center gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false)
                      setDeleteTarget(null)
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteNews}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <AlertCircle
                  className={`h-6 w-6 ${
                    messageModal.type === 'success'
                      ? 'text-green-600'
                      : messageModal.type === 'warning'
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }`}
                />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{messageModal.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{messageModal.message}</p>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowMessageModal(false)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publish Confirmation Modal */}
      {showPublishModal && confirmTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Eye className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Publish News Article</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to publish "{confirmTarget.title}"? This will make it visible to all users on the website.
                </p>
                <div className="flex items-center gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowPublishModal(false)
                      setConfirmTarget(null)
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePublishNews}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  >
                    Publish
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unpublish Confirmation Modal */}
      {showUnpublishModal && confirmTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <EyeOff className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Unpublish News Article</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to unpublish "{confirmTarget.title}"? This will remove it from public view and move it to Drafts where you can edit and republish it later.
                </p>
                <div className="flex items-center gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowUnpublishModal(false)
                      setConfirmTarget(null)
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUnpublishNews}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
                  >
                    Unpublish
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveModal && confirmTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Archive className="h-6 w-6 text-gray-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Archive News Article</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to archive "{confirmTarget.title}"? This will remove it from active listings. You can restore it later if needed.
                </p>
                <div className="flex items-center gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowArchiveModal(false)
                      setConfirmTarget(null)
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleArchiveNews}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                  >
                    Archive
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreModal && restoreTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                {restoreTarget.targetStatus === 'published' ? (
                  <Eye className="h-6 w-6 text-green-600" />
                ) : (
                  <Edit className="h-6 w-6 text-yellow-600" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {restoreTarget.targetStatus === 'published' ? 'Restore & Publish' : 'Restore to Draft'}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {restoreTarget.targetStatus === 'published' 
                    ? `Are you sure you want to restore and publish "${restoreTarget.article.title}"? This will make it visible to all users.`
                    : `Are you sure you want to restore "${restoreTarget.article.title}" to draft status? It will remain unpublished until you choose to publish it.`
                  }
                </p>
                <div className="flex items-center gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowRestoreModal(false)
                      setRestoreTarget(null)
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRestoreNews}
                    className={`px-4 py-2 text-white rounded-lg transition ${
                      restoreTarget.targetStatus === 'published' 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : 'bg-yellow-600 hover:bg-yellow-700'
                    }`}
                  >
                    {restoreTarget.targetStatus === 'published' ? 'Restore & Publish' : 'Restore to Draft'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
